import type { QuizDto, QuizDraftDto } from '@/types/api-generated'

/** The Quiz tab's one AI field: the working quiz, as JSON. QuizPanel, ChatMessages and AIChatWidget all name it. */
export const QUIZ_AI_FIELD = 'quiz'

/** The rows a draft added or changed, by ID, which the editor tags "Suggested by Webster" until Save or Cancel. */
export interface QuizDraftTouched {
    questionIds: string[]
    /** Includes dimensions whose advice alone changed. */
    dimensionIds: string[]
    bandIds: string[]
    reportSectionIds: string[]
    disclaimer: boolean
}

export const NOTHING_TOUCHED: QuizDraftTouched = { questionIds: [], dimensionIds: [], bandIds: [], reportSectionIds: [], disclaimer: false }

const union = (a: string[], b: string[]) => [...new Set([...a, ...b])]

/** Tags from a second Put in editor add to the first; nothing is untagged until Save or Cancel. */
export function combineTouched(a: QuizDraftTouched, b: QuizDraftTouched): QuizDraftTouched {
    return {
        questionIds: union(a.questionIds, b.questionIds),
        dimensionIds: union(a.dimensionIds, b.dimensionIds),
        bandIds: union(a.bandIds, b.bandIds),
        reportSectionIds: union(a.reportSectionIds, b.reportSectionIds),
        disclaimer: a.disclaimer || b.disclaimer,
    }
}

function replaceOrAppend<T extends { Id: string }>(items: T[], draftItems: T[]): T[] {
    const next = [...items]
    for (const item of draftItems) {
        const index = next.findIndex(existing => existing.Id === item.Id)
        if (index >= 0) next[index] = { ...item }
        else next.push({ ...item })
    }
    return next
}

/**
 * Merges Webster's draft into the editor's working quiz by ID. ⚑ The API applies the same rules to the saved quiz
 * in QuizDraftToolExecutor.Merge before the draft is shown: one side checks, the other applies, so the rule is written
 * twice and both test files pin the same cases. Change one, change both.
 *
 * A question, band or report section with an existing ID replaces that whole row in place; a new ID is appended. A
 * dimension with an existing ID gets its name replaced and keeps its advice; a new one is appended with none. Advice
 * sets low, mid and high on its dimension, which may be new in the same draft. A disclaimer replaces the quiz's.
 * Nothing is deleted or reordered. The one difference: the server refuses advice for an unknown dimension, while here
 * it is left out, since it can only happen when the author deleted that dimension without saving.
 */
export function mergeQuizDraft(working: QuizDto, draft: QuizDraftDto): { quiz: QuizDto; touched: QuizDraftTouched } {
    const dimensions = working.Dimensions.map(dimension => ({ ...dimension }))
    for (const item of draft.Dimensions) {
        const existing = dimensions.find(dimension => dimension.Id === item.Id)
        if (existing) existing.Name = item.Name
        else dimensions.push({ Id: item.Id, Name: item.Name, LowAdvice: null, MidAdvice: null, HighAdvice: null })
    }

    const advisedIds: string[] = []
    for (const advice of draft.DimensionAdvice) {
        const dimension = dimensions.find(existing => existing.Id === advice.DimensionId)
        if (!dimension) continue
        dimension.LowAdvice = advice.LowAdvice ?? null
        dimension.MidAdvice = advice.MidAdvice ?? null
        dimension.HighAdvice = advice.HighAdvice ?? null
        advisedIds.push(advice.DimensionId)
    }

    const quiz: QuizDto = {
        ...working,
        Dimensions: dimensions,
        Questions: replaceOrAppend(working.Questions, draft.Questions),
        Bands: replaceOrAppend(working.Bands, draft.Bands),
        ReportSections: replaceOrAppend(working.ReportSections, draft.ReportSections),
        Disclaimer: draft.Disclaimer != null ? draft.Disclaimer : working.Disclaimer,
    }

    return {
        quiz,
        touched: {
            questionIds: draft.Questions.map(question => question.Id),
            dimensionIds: union(draft.Dimensions.map(dimension => dimension.Id), advisedIds),
            bandIds: draft.Bands.map(band => band.Id),
            reportSectionIds: draft.ReportSections.map(section => section.Id),
            disclaimer: draft.Disclaimer != null,
        },
    }
}

/** The working quiz from the chat context's Quiz field, or null when the field is absent or isn't a quiz. */
export function parseWorkingQuiz(currentValue: string | number | null | undefined): QuizDto | null {
    if (typeof currentValue !== 'string' || !currentValue.startsWith('{')) return null
    try {
        const parsed = JSON.parse(currentValue) as Partial<QuizDto>
        return Array.isArray(parsed.Questions) && Array.isArray(parsed.Dimensions) && Array.isArray(parsed.Bands) && Array.isArray(parsed.ReportSections)
            ? parsed as QuizDto
            : null
    } catch {
        return null
    }
}

const plural = (count: number, singular: string, pluralForm = `${singular}s`) => `${count} ${count === 1 ? singular : pluralForm}`

/** "5 dimensions · 10 questions · 4 bands · advice for 5 dimensions · 2 report sections · disclaimer", as parts. */
export function quizDraftCountParts(draft: QuizDraftDto): string[] {
    const parts: string[] = []
    if (draft.Dimensions.length) parts.push(plural(draft.Dimensions.length, 'dimension'))
    if (draft.Questions.length) parts.push(plural(draft.Questions.length, 'question'))
    if (draft.Bands.length) parts.push(plural(draft.Bands.length, 'band'))
    if (draft.DimensionAdvice.length) parts.push(`advice for ${plural(draft.DimensionAdvice.length, 'dimension')}`)
    if (draft.ReportSections.length) parts.push(plural(draft.ReportSections.length, 'report section'))
    if (draft.Disclaimer != null) parts.push('disclaimer')
    return parts
}

export type QuizDraftItemKind = 'question' | 'dimension' | 'band' | 'advice' | 'reportSection' | 'disclaimer'

export interface QuizDraftDescription {
    /** What the card's title says the draft covers: "whole quiz", "question 4", "2 questions". */
    scope: string
    /** The count line, for a draft of several items; null for one item, which the card shows in full. */
    countParts: string[] | null
    /** "Replaces question 4" or "Adds question 11", for one item; null otherwise or when the editor can't be read. */
    placement: string | null
    /** The kind of the one item, when there is exactly one. */
    singleKind: QuizDraftItemKind | null
}

function kindsOf(draft: QuizDraftDto): Array<[QuizDraftItemKind, number]> {
    const kinds: Array<[QuizDraftItemKind, number]> = [
        ['dimension', draft.Dimensions.length],
        ['question', draft.Questions.length],
        ['band', draft.Bands.length],
        ['advice', draft.DimensionAdvice.length],
        ['reportSection', draft.ReportSections.length],
        ['disclaimer', draft.Disclaimer != null ? 1 : 0],
    ]
    return kinds.filter(([, count]) => count > 0)
}

/** The title for several items of one kind. A draft holds at most one disclaimer, so it never needs a plural. */
const SEVERAL_OF_ONE_KIND: Record<Exclude<QuizDraftItemKind, 'disclaimer'>, (count: number) => string> = {
    question: count => `${count} questions`,
    dimension: count => `${count} dimensions`,
    band: count => `${count} bands`,
    advice: count => `advice for ${count} dimensions`,
    reportSection: count => `${count} report sections`,
}

/** Where a question lands after Put in editor: its own number when it replaces one, the next number when it adds. */
export function questionNumberAfterMerge(working: QuizDto, questionId: string): { number: number; replaces: boolean } {
    const index = working.Questions.findIndex(question => question.Id === questionId)
    return index >= 0 ? { number: index + 1, replaces: true } : { number: working.Questions.length + 1, replaces: false }
}

/** The dimension's name as the author will see it: from the draft when it renames or adds it, else the editor. */
export function dimensionNameFor(dimensionId: string, draft: QuizDraftDto, working: QuizDto | null): string {
    return draft.Dimensions.find(dimension => dimension.Id === dimensionId)?.Name
        || working?.Dimensions.find(dimension => dimension.Id === dimensionId)?.Name
        || dimensionId
}

/**
 * The card's title and count line. Numbers and names come from the working quiz the chat context carries, so the
 * card says what Put in editor will do; with no working quiz (the author left the tab) an item is named by its ID.
 */
export function describeQuizDraft(draft: QuizDraftDto, working: QuizDto | null): QuizDraftDescription {
    const kinds = kindsOf(draft)
    const total = kinds.reduce((sum, [, count]) => sum + count, 0)
    const isWholeQuiz = draft.Dimensions.length > 0 && draft.Questions.length > 0 && draft.Bands.length > 0

    if (total !== 1) {
        const scope = isWholeQuiz
            ? 'whole quiz'
            : kinds.length === 1 && kinds[0][0] !== 'disclaimer' ? SEVERAL_OF_ONE_KIND[kinds[0][0]](total) : `${total} items`
        return { scope, countParts: quizDraftCountParts(draft), placement: null, singleKind: null }
    }

    const [kind] = kinds[0]
    const verb = (replaces: boolean) => (replaces ? 'Replaces' : 'Adds')
    switch (kind) {
        case 'question': {
            const question = draft.Questions[0]
            if (!working) return { scope: `question ${question.Id}`, countParts: null, placement: null, singleKind: kind }
            const { number, replaces } = questionNumberAfterMerge(working, question.Id)
            return { scope: `question ${number}`, countParts: null, placement: `${verb(replaces)} question ${number}`, singleKind: kind }
        }
        case 'dimension': {
            const dimension = draft.Dimensions[0]
            const existing = working?.Dimensions.find(item => item.Id === dimension.Id)
            const name = existing?.Name || dimension.Name
            return { scope: `dimension ${name}`, countParts: null, placement: working ? `${verb(!!existing)} dimension ${name}` : null, singleKind: kind }
        }
        case 'band': {
            const band = draft.Bands[0]
            const existing = working?.Bands.find(item => item.Id === band.Id)
            const name = existing?.Name || band.Name || band.Id
            return { scope: `band ${name}`, countParts: null, placement: working ? `${verb(!!existing)} band ${name}` : null, singleKind: kind }
        }
        case 'advice': {
            const name = dimensionNameFor(draft.DimensionAdvice[0].DimensionId, draft, working)
            return { scope: `advice for ${name}`, countParts: null, placement: working ? `Replaces the advice for ${name}` : null, singleKind: kind }
        }
        case 'reportSection': {
            const section = draft.ReportSections[0]
            const existing = working?.ReportSections.find(item => item.Id === section.Id)
            const title = existing?.Title || section.Title || section.Id
            return { scope: `report section ${title}`, countParts: null, placement: working ? `${verb(!!existing)} report section ${title}` : null, singleKind: kind }
        }
        case 'disclaimer':
            return {
                scope: 'disclaimer',
                countParts: null,
                placement: working ? `${verb(!!working.Disclaimer?.trim())} the disclaimer` : null,
                singleKind: kind,
            }
    }
}
